import React, { useMemo, useState } from "react";

interface CheckInCalendarProps {
  checkedInDates: string[];
  makeupChances: number;
  today: string;
  onMakeup: (date: string) => void;
  labels: {
    weekdays: string[];
    makeup: string;
  };
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const toKey = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

// 签到日历：展示历史签到、今天，并支持对过去的漏签日期进行补签
export const CheckInCalendar: React.FC<CheckInCalendarProps> = ({
  checkedInDates,
  makeupChances,
  today,
  onMakeup,
  labels,
}) => {
  const now = new Date();
  const [view, setView] = useState({ year: now.getFullYear(), month: now.getMonth() });

  const checkedSet = useMemo(() => new Set(checkedInDates), [checkedInDates]);

  // 首次签到日期：仅允许补签该日期之后的漏签日期
  const firstCheckInDate = useMemo(
    () => (checkedInDates.length > 0 ? [...checkedInDates].sort()[0] : null),
    [checkedInDates]
  );

  const startWeekday = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array.from({ length: startWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const shiftMonth = (delta: number) => {
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div className="select-none">
      {/* 月份导航 */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-lg text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          ‹
        </button>
        <span className="font-pingfang text-sm font-semibold text-white">
          {view.year}.{pad2(view.month + 1)}
        </span>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-lg text-zinc-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          ›
        </button>
      </div>

      {/* 星期表头 */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {labels.weekdays.map((w, i) => (
          <span key={i} className="py-1 text-xs text-zinc-500">
            {w}
          </span>
        ))}
      </div>

      {/* 日期网格 */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) {
            return <span key={`empty-${idx}`} className="h-9" />;
          }

          const dateStr = toKey(view.year, view.month, day);
          const isChecked = checkedSet.has(dateStr);
          const isToday = dateStr === today;
          const isPast = dateStr < today;
          const isAfterFirstCheckIn = firstCheckInDate !== null && dateStr > firstCheckInDate;
          const canMakeup = isPast && isAfterFirstCheckIn && !isChecked && makeupChances > 0;

          let cls = "relative flex h-9 items-center justify-center rounded-lg text-sm ";
          if (isChecked) {
            cls += "bg-cyan-500 font-semibold text-white";
          } else if (isToday) {
            cls += "font-semibold text-cyan-300 ring-1 ring-cyan-400/60";
          } else if (canMakeup) {
            cls += "cursor-pointer text-amber-300/90 hover:bg-amber-400/15";
          } else if (isPast) {
            cls += "text-zinc-600";
          } else {
            cls += "text-zinc-700";
          }

          if (canMakeup) {
            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => onMakeup(dateStr)}
                className={cls}
              >
                {day}
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[9px] font-bold leading-none text-black">
                  {labels.makeup}
                </span>
              </button>
            );
          }

          return (
            <span key={dateStr} className={cls}>
              {day}
            </span>
          );
        })}
      </div>
    </div>
  );
};
